<template>
  <vx-card id="print-report" class="w-full status-card">
    <!-- 실시현황 -->
    <div class="flex flex-wrap items-center mb-3 gap-2">
      <h5><i class="dot"></i>실시현황</h5>
      <div class="ml-auto flex items-center justify-center gap-3">
        <vs-select class="w-full" v-model="selectedYear" autocomplete @change="reloadPost">
          <vs-select-item v-for="year in years" :key="`year-${year}`" :value="year" :text="year" />
        </vs-select>
        <vs-select class="w-full" v-model="selectedHalf" autocomplete>
          <vs-select-item value="상반기" text="상반기" />
          <vs-select-item value="하반기" text="하반기" />
        </vs-select>
      </div>
    </div>

    <!-- 보기 전환: 실시현황 | 조치현황 | 첨부현황 -->
    <div class="flex flex-wrap items-center gap-2 mb-3">
      <div class="flex gap-1">
        <vs-button v-for="m in MODES" :key="m.key" color="primary" :type="mode === m.key ? 'filled' : 'border'" @click="mode = m.key">{{ m.label }}</vs-button>
      </div>
      <div v-if="mode === 'act'" class="ml-auto flex flex-wrap items-center gap-3 legend">
        <span>전체 <b>{{ yearStats.n }}</b></span>
        <span><i class="circle"></i>완료 <b>{{ yearStats.완료 }}</b></span>
        <span><i class="sym half">▲</i>진행중 <b>{{ yearStats.진행중 }}</b></span>
        <span><i class="sym x">✕</i>기타 <b>{{ yearStats.기타 }}</b></span>
        <span><i class="sym none">–</i>특이사항 없음</span>
        <span class="muted">미입력 {{ yearStats.미입력 }} · {{ selectedYear }}년 1월~당월 기준</span>
      </div>
      <div v-else-if="mode === 'att'" class="ml-auto flex flex-wrap items-center gap-3 legend">
        <span><i class="circle"></i>완료</span>
        <span><i class="sym half">▲</i>일부</span>
      </div>
      <div v-else class="ml-auto muted legend">회의가 열린 칸을 누르면 아래 주요의견 및 조치사항으로 이동합니다.</div>
    </div>

    <div class="w-full overflow-x-auto">
      <table>
        <colgroup>
          <col :width="mode === 'run' ? 200 : 150" />
          <col v-for="(c, i) in columns" :key="`col-${i}`" :width="c.boardId === 818 ? 100 : 60" />
          <col v-if="mode !== 'run'" width="90" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">구분</th>
            <th v-for="b in BOARDS" :key="`head-${b.boardId}`" :colspan="b.boardId === 818 ? 2 : 6">{{ b.title }}</th>
            <th v-if="mode !== 'run'" rowspan="2">
              {{ mode === "act" ? "사업장별 조치율" : "사업장별 첨부율" }}<br />
              <small class="muted">{{ mode === "act" ? "(올해 전체)" : "(선택 반기)" }}</small>
            </th>
          </tr>
          <tr>
            <th v-for="(c, i) in columns" :key="`colhead-${i}`">{{ c.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="site in sites" :key="site.fieldId">
            <td class="bg-th">{{ site.label }}</td>
            <td v-for="(c, i) in columns" :key="`${site.fieldId}-${i}`" :class="cellClass(site, c)" :title="cellTitle(site, c)" @click="onClickCell(site, c)">
              <div class="flex items-center justify-center">
                <div v-if="cellSymbol(site, c) === 'ok'" class="circle"></div>
                <span v-else-if="cellSymbol(site, c) === 'half'" class="sym half">▲</span>
                <span v-else-if="cellSymbol(site, c) === 'x'" class="sym x">✕</span>
                <span v-else-if="cellSymbol(site, c) === 'none'" class="sym none">–</span>
              </div>
            </td>
            <td v-if="mode !== 'run'" class="pct">{{ siteRate(site) }}</td>
          </tr>
          <tr v-if="mode !== 'run' && sites.length" class="sum-row">
            <td class="bg-th">{{ mode === "act" ? "회의별 조치율 (전체)" : "회의별 첨부율" }}</td>
            <td v-for="b in BOARDS" :key="`sum-${b.boardId}`" :colspan="b.boardId === 818 ? 2 : 6" class="pct">{{ boardRate(b.boardId) }}</td>
            <td class="pct total">{{ totalRate }}</td>
          </tr>
          <tr v-if="0 === sites.length">
            <td :colspan="columns.length + 2">표시할 데이터가 없습니다.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </vx-card>
</template>

<script>
import { normalizeOpinions, opinionStats, addStats, emptyStats, actionRate, percent, hasMinutes, hasMaterials, getQuarter, sortSites } from "./opinion.js";

const BOARDS = [
  { boardId: 818, title: "산업안전보건위원회(1회/분기)" },
  { boardId: 819, title: "안전보건협의체(1회/월)" },
  { boardId: 820, title: "설비안전위원회(1회/월)" },
  { boardId: 821, title: "크레인안전협의체(1회/2개월)" },
];
const MODES = [
  { key: "run", label: "실시현황" },
  { key: "act", label: "조치현황" },
  { key: "att", label: "첨부현황" },
];

export default {
  data() {
    return {
      BOARDS,
      MODES,
      selectedYear: new Date().getFullYear(),
      years: [],
      selectedHalf: "상반기",
      mode: "run",
      selectedCell: "",

      // data[boardId][fieldId] = { label, cells: { q1|m3: [summary] }, posts: [summary] }
      // summary = { postId, quarter, month, 특이사항없음, stats, 회의록, 회의자료 }
      data: {},
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    // 선택 반기의 열. period 는 주요의견(FeedbackActions)의 기간 선택값과 같은 의미.
    columns() {
      const first = this.selectedHalf === "상반기";
      const cols = (first ? [1, 2] : [3, 4]).map((q) => ({ boardId: 818, key: `q${q}`, label: `${q}분기`, period: q }));
      const months = first ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
      for (const boardId of [819, 820, 821]) {
        for (const m of months) cols.push({ boardId, key: `m${m}`, label: `${m}월`, period: boardId === 821 ? (m % 2 ? m : m - 1) : m });
      }
      return cols;
    },
    // 사업장순서(본사·인천·포항·당진·중앙기술연구소) 순, 그 밖의 사업장은 회의 순서대로 처음 나온 순서
    sites() {
      const sites = [];
      const seen = {};
      for (const b of BOARDS) {
        const byField = this.data[b.boardId] || {};
        for (const fieldId of Object.keys(byField)) {
          if (seen[fieldId]) continue;
          seen[fieldId] = true;
          sites.push({ fieldId, label: byField[fieldId].label });
        }
      }
      return sortSites(sites);
    },
    // 올해 전체 의견 건수 (조치현황 우상단)
    yearStats() {
      const acc = emptyStats();
      for (const b of BOARDS) for (const site of Object.values(this.data[b.boardId] || {})) for (const p of site.posts) addStats(acc, p.stats);
      return acc;
    },
    totalRate() {
      if (this.mode === "act") return actionRate(this.yearStats);
      let held = 0;
      let full = 0;
      for (const site of this.sites) for (const c of this.columns) for (const p of this.cellPosts(site, c)) {
        held++;
        if (p.회의록 && p.회의자료) full++;
      }
      return percent(full, held);
    },
  },
  created() {
    this.generateYears();
    this.reloadPost();
  },
  methods: {
    generateYears() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 50;
      for (let year = currentYear; year >= startYear; year--) {
        this.years.push(year);
      }
    },

    cellPosts(site, c) {
      const byField = this.data[c.boardId];
      const entry = byField && byField[site.fieldId];
      return (entry && entry.cells[c.key]) || [];
    },
    // ok(●) half(▲) x(✕) none(–) '' 중 하나
    cellSymbol(site, c) {
      const posts = this.cellPosts(site, c);
      if (!posts.length) return "";
      if (this.mode === "run") return "ok";
      if (this.mode === "att") {
        if (posts.every((p) => p.회의록 && p.회의자료)) return "ok";
        return posts.some((p) => p.회의록 || p.회의자료) ? "half" : "";
      }
      const k = posts.reduce((acc, p) => addStats(acc, p.stats), emptyStats());
      if (!k.n) return "none"; // 특이사항 없음(의견 없음)
      if (k.진행중) return "half";
      if (k.미입력) return ""; // 미입력은 공란
      return k.기타 === k.n ? "x" : "ok";
    },
    cellTitle(site, c) {
      const posts = this.cellPosts(site, c);
      if (!posts.length) return "";
      const where = `${site.label} ${BOARDS.find((b) => b.boardId === c.boardId).title.replace(/\(.*\)$/, "")} ${c.label}`;
      if (this.mode === "att") return posts.map((p) => `${where}: 회의록 ${p.회의록 ? "있음" : "없음"}, 회의자료 ${p.회의자료 ? "있음" : "없음"}`).join("\n");
      const k = posts.reduce((acc, p) => addStats(acc, p.stats), emptyStats());
      if (this.mode === "act") return `${where}: 의견 ${k.n}건 (완료 ${k.완료}, 진행중 ${k.진행중}, 기타 ${k.기타}, 미입력 ${k.미입력})`;
      return `${where}: 회의 실시`;
    },
    cellClass(site, c) {
      const held = this.cellPosts(site, c).length > 0;
      return { held, selected: held && this.selectedCell === `${site.fieldId}-${c.boardId}-${c.key}` };
    },
    onClickCell(site, c) {
      if (!this.cellPosts(site, c).length) return;
      this.selectedCell = `${site.fieldId}-${c.boardId}-${c.key}`;
      this.$emit("select-meeting", { boardId: c.boardId, year: this.selectedYear, period: c.period, fieldId: site.fieldId });
    },

    // 사업장별 조치율(올해 전체) / 첨부율(선택 반기)
    siteRate(site) {
      if (this.mode === "act") {
        const acc = emptyStats();
        for (const b of BOARDS) {
          const entry = (this.data[b.boardId] || {})[site.fieldId];
          if (entry) for (const p of entry.posts) addStats(acc, p.stats);
        }
        return actionRate(acc);
      }
      let held = 0;
      let full = 0;
      for (const c of this.columns) for (const p of this.cellPosts(site, c)) {
        held++;
        if (p.회의록 && p.회의자료) full++;
      }
      return percent(full, held);
    },
    boardRate(boardId) {
      if (this.mode === "act") {
        const acc = emptyStats();
        for (const site of Object.values(this.data[boardId] || {})) for (const p of site.posts) addStats(acc, p.stats);
        return actionRate(acc);
      }
      let held = 0;
      let full = 0;
      for (const site of this.sites) for (const c of this.columns.filter((x) => x.boardId === boardId)) for (const p of this.cellPosts(site, c)) {
        held++;
        if (p.회의록 && p.회의자료) full++;
      }
      return percent(full, held);
    },

    async reloadPost() {
      const results = await Promise.all(BOARDS.map((b) => this.loadPost(b.boardId)));
      const data = {};
      BOARDS.forEach((b, i) => (data[b.boardId] = results[i]));
      this.data = data;
    },

    // 게시물 불러오기 (818,819,820,821). 선택 연도 전체를 받아 반기 열과 조치율(올해 전체)을 함께 만든다.
    async loadPost(boardId) {
      if (!this.selectedYear) return {};

      const projectId = this.$route.params.proj_id;
      const page = 1;
      const perPage = 10000;
      const conditions = [];

      if (boardId === 818) {
        // 818은 header.year·quarter 기준으로 조회한다.
        // body.quaterDate는 작성한 달에서 자동 생성되던 값이라 2025년 11월 이후 문서에는 없고, 있어도 실시구분과 어긋난다.
        conditions.push({ key: "header.year", operator: "=", value: this.selectedYear });
      } else {
        conditions.push({ key: "body.monthDate", operator: ">=", value: `${this.selectedYear}-01-01` });
        conditions.push({ key: "body.monthDate", operator: "<=", value: `${this.selectedYear}-12-31` });
      }

      // 4개 게시판을 동시에 불러오므로 store 상태가 아니라 응답값을 쓴다
      const response = await this.$store.dispatch("board/LOAD_POST_LIST", { projectId, boardId, page, perPage, conditions });
      const list = (response && response.data) || [];

      const _datas = {};
      for (const data of list) {
        const content = JSON.parse(data.post_content);
        const header = content.header || {};
        const body = content.body || {};
        const month = Number(header.month) || 0;
        const quarter = getQuarter(header);
        const key = boardId === 818 ? `q${quarter}` : `m${month}`;
        const n = normalizeOpinions(body.subInfo);
        const summary = { postId: data.id, quarter, month, 특이사항없음: n.특이사항없음, stats: opinionStats(n.의견목록), 회의록: hasMinutes(body), 회의자료: hasMaterials(body) };

        const fieldId = data.field_id;
        if (!_datas[fieldId]) _datas[fieldId] = { label: data.field.field_name, cells: {}, posts: [] };
        (_datas[fieldId].cells[key] = _datas[fieldId].cells[key] || []).push(summary);
        _datas[fieldId].posts.push(summary);
      }
      return _datas;
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: calc(100% - 1px);
  table-layout: fixed;
  border-collapse: collapse;
  background: #fafafa;

  &,
  th,
  td {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    padding: 14px;

    border: 1px solid #cdcdcd;
  }

  th,
  .bg-th {
    background: #fafafa;
  }

  td {
    font-weight: 400;
    background: white;

    &.held {
      cursor: pointer;
      &:hover {
        background: #f3f2ff;
      }
    }
    &.selected {
      background: #eeedfd;
      box-shadow: inset 0 0 0 2px #7367f0;
    }
    &.pct {
      font-weight: 500;
    }
    &.total {
      background: #fafafa;
      font-weight: 600;
    }
  }

  // 마지막 행(회의별 조치율·첨부율)은 머리칸부터 전체 칸까지 모두 굵게
  .sum-row td {
    background: #fcfcfc;
    font-weight: 700;
  }
}

.circle {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #28c76f;
  border-radius: 100%;
}
.sym {
  display: inline-block;
  font-size: 13px;
  line-height: 10px;
  font-style: normal; // 범례의 <i> 기본 이탤릭 때문에 ▲·✕ 가 기울어 보이지 않도록
  &.half {
    color: #ff9f43;
  }
  &.x {
    color: #ea5455;
  }
  &.none {
    color: #b0b0b0;
  }
}
.legend {
  font-size: 13px;
  color: #555;
  i {
    margin-right: 4px;
    vertical-align: middle;
    font-style: normal;
  }
  b {
    margin-left: 2px;
  }
}
.muted {
  color: #999;
}
</style>
